import { NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { skipWhile, take } from 'rxjs/operators';

import { Barn } from '../../models/barn.model';
import { setSelectedBarn } from '../../store/actions/setting.actions';
import { getSelectedBarn } from '../../store/selectors/setting.selector';
import { propertiesValidator } from '../../validators/properties-validator';

export interface BarnItemDialogInputData extends AbstractFormDialogData {
  barnId?: number;
}
export interface BarnItemDialogResultData {
  barn: Barn;
}

@Component({
  selector: 'app-barn-item-dialog',
  templateUrl: './barn-item-dialog.component.html',
  styleUrls: ['./barn-item-dialog.component.scss'],
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    AgrNumberDirective,
    MatSuffix,
    MatSelect,
    MatOption,
    MatSlideToggle,
    TranslatePipe,
  ],
})
export class BarnItemDialogComponent extends AbstractDialog<BarnItemDialogResultData> {
  id: number;

  readonly formGroup = new FormGroup(
    {
      name: new FormControl<string>('', [Validators.required]),
      surfaceArea: new FormControl<number>(undefined, Validators.required),
      jumpingFacilities: new FormControl<string>('', Validators.required),
      numberOfFeedLines: new FormControl<number>(undefined, Validators.required),
      methodOfFeedAdministration: new FormControl<string>('', Validators.required),
      lengthOfFeedGutterInMeters: new FormControl<number>(undefined),
      numberOfFeedPans: new FormControl<number>(undefined),
      numberOfWaterLines: new FormControl<number>(undefined, Validators.required),
      waterSupply: new FormControl<string>('', Validators.required),
      numberOfWaterNipples: new FormControl<number>(undefined),
      numberOfDrinkingTowers: new FormControl<number>(undefined),
      lighting: new FormControl<string>('', Validators.required),
      hasUVLighting: new FormControl<boolean>(false),
    },
    { validators: propertiesValidator },
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BarnItemDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      BarnItemDialogComponent,
      AbstractDialogResultData<BarnItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {
    super();
    this.id = this.data.barnId;
    if (this.id && this.id > 0) {
      this.data.title = this.localeService.translate('barn.edit-barn');

      this.store.dispatch(setSelectedBarn({ barnId: this.id }));
      this.store
        .select(getSelectedBarn)
        .pipe(
          skipWhile((c) => c == null),
          take(1),
        )
        .subscribe((barn) => {
          this.formGroup.controls.name.setValue(barn.name);
          this.formGroup.controls.surfaceArea.setValue(barn.surfaceArea);
          if (barn.properties != null) {
            this.formGroup.controls.jumpingFacilities.setValue(
              barn.properties['jumpingFacilities'],
            );
            this.formGroup.controls.numberOfFeedLines.setValue(
              barn.properties['numberOfFeedLines'],
            );
            this.formGroup.controls.numberOfWaterLines.setValue(
              barn.properties['numberOfWaterLines'],
            );
            this.formGroup.controls.methodOfFeedAdministration.setValue(
              barn.properties['methodOfFeedAdministration'],
            );
            this.formGroup.controls.numberOfFeedPans.setValue(barn.properties['numberOfFeedPans']);
            this.formGroup.controls.lengthOfFeedGutterInMeters.setValue(
              barn.properties['lengthOfFeedGutterInMeters'],
            );
            this.formGroup.controls.waterSupply.setValue(barn.properties['waterSupply']);
            this.formGroup.controls.numberOfWaterNipples.setValue(
              barn.properties['numberOfWaterNipples'],
            );
            this.formGroup.controls.numberOfDrinkingTowers.setValue(
              barn.properties['numberOfDrinkingTowers'],
            );
            this.formGroup.controls.lighting.setValue(barn.properties['lighting']);
            this.formGroup.controls.hasUVLighting.setValue(barn.properties['hasUVLighting']);
          }
        });
    } else {
      this.data.title = this.localeService.translate('barn.add-barn');
    }
  }

  save() {
    if (this.formGroup.valid) {
      const barn = {
        name: <string>this.formGroup.controls.name.value,
        surfaceArea: this.formGroup.controls.surfaceArea.value,
        properties: {
          jumpingFacilities: this.formGroup.controls.jumpingFacilities.value,
          numberOfFeedLines: this.formGroup.controls.numberOfFeedLines.value,
          methodOfFeedAdministration: this.formGroup.controls.methodOfFeedAdministration.value,
          lengthOfFeedGutterInMeters:
            this.formGroup.controls.methodOfFeedAdministration.value === 'Chain'
              ? this.formGroup.controls.lengthOfFeedGutterInMeters.value
              : null,
          numberOfFeedPans:
            this.formGroup.controls.methodOfFeedAdministration.value === 'Pan'
              ? this.formGroup.controls.numberOfFeedPans.value
              : null,
          numberOfWaterLines: this.formGroup.controls.numberOfWaterLines.value,
          waterSupply: this.formGroup.controls.waterSupply.value,
          numberOfWaterNipples:
            this.formGroup.controls.waterSupply.value === 'WaterNipples' ||
            this.formGroup.controls.waterSupply.value === 'Both'
              ? this.formGroup.controls.numberOfWaterNipples.value
              : null,
          numberOfDrinkingTowers:
            this.formGroup.controls.waterSupply.value === 'DrinkingTowers' ||
            this.formGroup.controls.waterSupply.value === 'Both'
              ? this.formGroup.controls.numberOfDrinkingTowers.value
              : null,
          lighting: this.formGroup.controls.lighting.value,
          hasUVLighting: this.formGroup.controls.hasUVLighting.value ?? false,
        },
      } as unknown as Barn;

      if (this.id && this.id > 0) {
        barn.id = this.id;
      }

      this.confirm({
        barn,
      });
    }
  }
}
